import { APP_PREFIX_PATH } from 'configs/AppConfig';
import { UserType } from 'schemas/user.schemas';

/** Post-login landing path for Ads Manager. */
export const ADS_MANAGER_DEFAULT_PATH = `${APP_PREFIX_PATH}/architect-images/list`;

/** Post-login landing path for superadmin. */
export const SUPERADMIN_DEFAULT_PATH = `${APP_PREFIX_PATH}/dashboards/default`;

export const ADMIN_ACCESS_REQUIRED_MESSAGE = 'Admin access required.';

const ADS_MANAGER_LOGIN_AS_TARGET_TYPES = new Set([
  UserType.APP_USER,
  UserType.PROMPT_MANAGER,
]);

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function isAdminPanelUser(user) {
  if (user == null || user.type == null) {
    return false;
  }
  const type = Number(user.type);
  return type === UserType.SUPERADMIN || type === UserType.ADS_MANAGER;
}

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {boolean}
 */
export function isAdsManager(user) {
  if (user == null || user.type == null) {
    return false;
  }
  return Number(user.type) === UserType.ADS_MANAGER;
}

/**
 * @param {{ type?: number | string } | null | undefined} targetUser
 * @returns {boolean}
 */
export function canAdsManagerLoginAsUser(targetUser) {
  if (targetUser == null || targetUser.type == null) {
    return false;
  }
  return ADS_MANAGER_LOGIN_AS_TARGET_TYPES.has(Number(targetUser.type));
}

/**
 * @param {{ type?: number | string } | null | undefined} currentUser
 * @param {{ type?: number | string } | null | undefined} targetUser
 * @returns {boolean}
 */
export function canShowLoginAsUser(currentUser, targetUser) {
  if (!targetUser) {
    return false;
  }
  if (!isAdsManager(currentUser)) {
    return true;
  }
  return canAdsManagerLoginAsUser(targetUser);
}

/**
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {string}
 */
export function getAuthenticatedEntryPath(user) {
  return isAdsManager(user) ? ADS_MANAGER_DEFAULT_PATH : SUPERADMIN_DEFAULT_PATH;
}

const ADS_MANAGER_ALLOWED_PATHS = [
  `${APP_PREFIX_PATH}/architect-images/list`,
  `${APP_PREFIX_PATH}/users/list`,
  `${APP_PREFIX_PATH}/users/view`,
  `${APP_PREFIX_PATH}/pages/setting/edit-profile`,
  `${APP_PREFIX_PATH}/pages/setting/change-password`,
];

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export function isPathAllowedForAdsManager(pathname) {
  const normalized = (pathname || '').replace(/\/$/, '') || '/';
  return ADS_MANAGER_ALLOWED_PATHS.some(
    (allowed) => normalized === allowed || normalized.startsWith(`${allowed}/`),
  );
}

/**
 * @param {Array<Record<string, unknown>>} navConfig
 * @param {{ type?: number | string } | null | undefined} user
 * @returns {Array<Record<string, unknown>>}
 */
export function filterNavigationForUser(navConfig, user) {
  if (!isAdsManager(user)) {
    return navConfig;
  }

  return navConfig
    .filter((item) => item.key === 'media' || item.key === 'users')
    .map((item) => {
      if (item.key === 'media') {
        return {
          ...item,
          path: ADS_MANAGER_DEFAULT_PATH,
          submenu: (item.submenu || []).filter(
            (sub) => sub.key === 'architect-images.list',
          ),
        };
      }

      if (item.key === 'users') {
        return {
          ...item,
          path: `${APP_PREFIX_PATH}/users/list`,
          submenu: (item.submenu || []).filter((sub) => sub.key === 'users.list'),
        };
      }

      return item;
    });
}
